import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, RefreshCw, ShieldCheck, Zap, KeyRound } from 'lucide-react';
import { testJobTreadConnection } from '../../services/jobtreadService';
import { saveJobTreadGrant } from '../../services/userIntegrationService';

// Full-tab call-to-action shown when JobTread isn't connected — every metric
// on the analytics tabs is JobTread-sourced, so empty cards would just repeat
// themselves.
//
// Portal users are sent to their profile to connect. When `clientUserId` is
// set (CRM Portal View acting for a client), the button instead reveals an
// inline grant-key input: the key is tested, then saved on the client's own
// profile via the saveJobTreadGrant callable, and `onConnected` re-runs the data.
const ConnectJobTreadHero = ({ description, clientUserId = null, onConnected }) => {
  const [showInput, setShowInput] = useState(false);
  const [grantKey, setGrantKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const trimmed = grantKey.trim();
    if (!trimmed) {
      setError('Grant key is required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await testJobTreadConnection(trimmed);
    } catch (e) {
      console.error('JobTread grant key test failed:', e);
      setError("That grant key didn't work — double-check it in JobTread Grant Management.");
      setSaving(false);
      return;
    }
    try {
      // Stored server-side on the client's profile (staff-only for another user)
      await saveJobTreadGrant(trimmed, clientUserId || undefined);
      setGrantKey('');
      setShowInput(false);
      onConnected?.();
    } catch (e) {
      console.error('Saving client JobTread grant key failed:', e);
      setError('The key works, but saving it to the client failed. Try again.');
    } finally {
      setSaving(false);
    }
  };

  const buttonClass =
    'inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-semibold shadow-sm transition-colors';

  return (
    <div className="rounded-xl border bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800 shadow-sm px-6 py-12 md:py-16 text-center">
      <div className="w-20 h-20 mx-auto mb-5 rounded-2xl bg-gray-100 dark:bg-gray-700/60 flex items-center justify-center">
        <img src="/JTLogo.png" alt="JobTread" className="w-12 h-12 object-contain" />
      </div>
      <h3 className="text-xl md:text-2xl font-bold tracking-tight text-gray-900 dark:text-white mb-2">
        Connect JobTread to see your numbers
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 max-w-lg mx-auto mb-8">{description}</p>
      <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 mb-8 text-xs font-medium text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <Zap className="w-3.5 h-3.5 text-red-600" /> Takes about a minute
        </span>
        <span className="inline-flex items-center gap-1.5">
          <RefreshCw className="w-3.5 h-3.5 text-red-600" /> Always up to date
        </span>
        <span className="inline-flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5 text-red-600" /> Secure grant-key access
        </span>
      </div>

      {!clientUserId ? (
        <Link to="/portal/profile#integrations" className={buttonClass}>
          Connect JobTread
          <ArrowRight className="w-4 h-4" />
        </Link>
      ) : !showInput ? (
        <button type="button" onClick={() => setShowInput(true)} className={buttonClass}>
          Connect JobTread
          <ArrowRight className="w-4 h-4" />
        </button>
      ) : (
        <div className="max-w-md mx-auto text-left">
          <label className="block text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1.5">
            Client&apos;s JobTread Grant Key
          </label>
          <div className="relative">
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={grantKey}
              onChange={(e) => { setGrantKey(e.target.value); setError(''); }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSave();
                }
                if (e.key === 'Escape') setShowInput(false);
              }}
              placeholder="Paste the grant key from JobTread Grant Management"
              autoFocus
              disabled={saving}
              className="w-full pl-9 pr-3 py-2.5 rounded-lg border text-sm bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500"
            />
          </div>
          {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Saved to this client&apos;s portal profile — it connects JobTread across their whole portal.
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={() => { setShowInput(false); setGrantKey(''); setError(''); }}
              disabled={saving}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold text-white rounded-lg bg-red-600 hover:bg-red-700 disabled:bg-red-300 dark:disabled:bg-red-900 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Connecting...
                </>
              ) : (
                'Connect'
              )}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConnectJobTreadHero;
